// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { fontConfig } from "@vanmeter/XDL/constants";
import AngleDown from "@vanmeter/XDL/Icons/IconAngleDownSmall";
import { styledScrollbar, xScreen } from "@vanmeter/XDL/Utilities";
import color, { getSystemColor } from "@vanmeter/XDL/Utilities/color";
import { mergeWithCss } from "@vanmeter/XDL/Utilities/mergeWithCss";
import styled, { css } from "styled-components";

export const styledLinksMenu = (width: number) => {
    const xColor = {
        background: {
            hover: "common.ui8",
        },
        text: {
            default: "text.main",
            hover: "common.ui1",
        },
    };

    return ({ theme }: any) => css`
        padding: 2px 0 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 4px;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: ${fontConfig.smallSingleLine.size}px;
                line-height: ${fontConfig.smallSingleLine.lineHeight};
                font-weight: ${fontConfig.weight.regular};
                padding: 8px;
                color: ${resolveColor(xColor.text.default, theme)};
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
                text-align: left;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor(xColor.background.hover, theme)};
                color: ${resolveColor(xColor.text.hover, theme)};
            }
        }

        ${styledScrollbar()}
    `;
};

/**
 * Hide input increment/decrement arrows from browser when textField type = number
 * */
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

export const companyLogoItem: InjectableCss = {
    css: css`
        display: none;

        @media print {
            display: block;
            padding: 0;
            margin: 0 15px;
            width: 100%;
        }
    `,
};

const linkSizeVariants = {
    regular: {
        ...fontConfig.regularSingleLine,
        iconSize: 16,
    },
    small: {
        ...fontConfig.smallSingleLine,
        iconSize: 13,
    },
    xsmall: {
        ...fontConfig.xSmallSingleLine,
        iconSize: 13,
    },
};

export const createLinkIconStyles = ({
    icon,
    linkStyles = {},
    sizeVariant = "regular",
    iconSize,
}: {
    icon?: React.ComponentType | string;
    linkStyles?: LinkPresentationProps;
    sizeVariant?: "regular" | "small" | "xsmall";
    iconSize?: number | { width?: number; height?: number };
}) => {
    const marginPosition = linkStyles?.icon?.position === "right" ? "margin-left" : "margin-right";
    const defaultSize = linkSizeVariants[sizeVariant].iconSize as number;
    const resolvedSize = iconSize ?? defaultSize;
    const width = typeof resolvedSize === "object" ? (resolvedSize.width ?? defaultSize) : resolvedSize;
    const height = typeof resolvedSize === "object" ? (resolvedSize.height ?? defaultSize) : resolvedSize;
    return {
        ...mergeWithCss(
            {
                mergeCss: true,
                typographyProps: {
                    size: linkSizeVariants[sizeVariant].size,
                    lineHeight: linkSizeVariants[sizeVariant].lineHeight,
                    weight: fontConfig.weight.bold,
                    css: css`
                        letter-spacing: 0;
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        width,
                        height,
                        css: css`
                            ${sizeVariant === "regular"
                                ? css`
                                      ${marginPosition}: 8px;
                                  `
                                : css`
                                      ${marginPosition}: 6px;
                                  `};
                        `,
                    },
                    position: linkStyles?.icon?.position,
                },
                css: css`
                    ${linkStyles?.icon?.position === "right"
                        ? css`
                              width: fit-content;
                          `
                        : ""}
                `,
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const sharedTextBadgeStyle: ProductTextBadgesStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            width: fit-content;
        `,
    },
    badge: {
        css: css`
            display: flex;
            gap: 4px;
            flex-wrap: wrap;
            & > div {
                border-radius: 8px;
                display: flex;
                flex-shrink: 0;
                gap: 4px;
                padding: 6px;
                align-items: center;
                justify-content: center;
            }
        `,
    },
    badgeText: {
        ...fontConfig.xxSmall,
        weight: fontConfig.weight.bold,
    },
};

export const sharedImageBadgesStyles: ProductImageBadgesStyles = {
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            width: fit-content;
        `,
    },
    imageItem: {
        css: css`
            max-width: fit-content;
            padding: 0;
            justify-content: center;
            width: 20px;
            height: 20px;
            > div {
                margin-left: 0;
            }
        `,
    },
    imageContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: start;
            gap: 4px;
        `,
    },
};

interface ProductOverlayBadgeStyles {
    badgeAreaWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}
export const sharedOverlayBadgeStyles: ProductOverlayBadgeStyles = {
    badgeAreaWrapper: {
        css: css`
            display: flex;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            display: flex;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            display: flex;
            & > div {
                border-radius: 8px;
                display: flex;
                padding: 4px;
                align-items: center;
                justify-content: center;
            }
        `,
    },
    badgeText: {
        ...fontConfig.xxSmall,
        weight: fontConfig.weight.bold,
    },
    badgeImageProps: {
        width: 20,
        height: 20,
    },
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            width: 100%;
            border-top: 1px solid ${color("common.border")};
            padding-top: 12px;
        `,
    },
    cancelButton: {
        variant: "primary",
        buttonType: "outline",
    },
    submitButton: {
        variant: "primary",
    },
};

export const dotButtonStyles: ButtonPresentationProps = {
    css: css`
        height: 10px;
        width: 10px;
        min-height: unset;
        padding: 0;
        margin-right: 4px;
        border: 1px solid ${color("common.ui1")};
        border-radius: 5px;
        background: transparent;

        &:active,
        &:focus {
            &&&& {
                width: 20px;
                background: ${color("common.ui1")};
                border-color: ${color("common.ui1")};
                outline: none;
            }
        }
        &:hover {
            &&&& {
                background: ${color("common.ui1")};
                border-color: ${color("common.ui1")};
                outline: none;
            }
        }

        &.slide-dot-button-selected {
            width: 20px;
            background: ${color("common.ui1")};
        }
    `,
};

export const selectedPanelItem = css`
    cursor: pointer;
    background: ${getSystemColor("common.ui8")};
    & ${IconWrapper} {
        color: ${getSystemColor("primary")};
    }
`;

export const emptyValueStyles = {
    css: css`
        font-size: 30px;
        color: ${color("common.ui5")};
    `,
};

export const StyledHr = getStyledWrapper("hr");

interface MoreActionsSharedStyle {
    overflowMenu?: OverflowMenuPresentationProps;
    moreActionsLink?: LinkPresentationProps;
}
export const moreActionsSharedStyle: MoreActionsSharedStyle = {
    overflowMenu: {
        _width: 160,
        cssOverrides: {
            wrapper: css`
                width: auto;
                height: auto;
                & button[aria-expanded="true"] ${IconWrapper} svg {
                    transform: rotate(180deg);
                }
            `,
            menu: css`
                li:last-child button {
                    padding-bottom: 8px;
                    &::after {
                        display: none;
                    }
                }
            `,

            menuItem: css`
                padding: 8px 12px 0;
                &::after {
                    content: "";
                    display: block;
                    border-bottom: 1px solid ${color("common.border")};
                    width: 100%;
                    margin-top: 8px;
                }
            `,
        },
    },
    moreActionsLink: {
        typographyProps: { ...fontConfig.xSmallSingleLine },
        icon: {
            iconProps: {
                src: AngleDown,
                size: 12,
            },
            position: "right",
        },
    },
};

export const variantSelectionLabel: TypographyPresentationProps = {
    ...fontConfig.smallSingleLine,
    transform: "uppercase",
    weight: fontConfig.weight.bold,
    color: "common.ui6",
};

export const LoadingDots = styled.span`
    display: flex;
    align-items: center;
    min-width: 15px;
    position: relative;
    height: 100%;

    &::after {
        overflow: hidden;
        display: inline-block;
        vertical-align: bottom;
        animation: ellipsis steps(4, end) 900ms infinite 1s;
        content: "\\2026";
        width: 0;
        min-height: 8px;
    }

    @keyframes ellipsis {
        to {
            width: 1.25em;
        }
    }
`;

export const priceRequireQuoteMessage = {
    ...fontConfig.regularSingleLine,
    color: "common.ui6",
    as: "p",
    weight: fontConfig.weight.bold,
    css: css`
        display: inline-block;
        margin-bottom: 0;
    `,
} as TypographyProps;